// File: src/pages/admin/SubscriberOrganizations.js
// Route: /admin/subscriber-organizations

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, updateDoc, doc, serverTimestamp, deleteField } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';

const SubscriberOrganizations = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [processing, setProcessing] = useState(null);
  const [customerSummaries, setCustomerSummaries] = useState({});

  useEffect(() => {
    loadSubscriberOrganizations();
  }, [filter]);

  useEffect(() => {
    const loadCustomerSummaries = async () => {
      const customerIds = organizations
        .map((org) => org.license?.stripeCustomerId)
        .filter(Boolean);

      if (customerIds.length === 0) {
        setCustomerSummaries({});
        return;
      }

      try {
        const functions = getFunctions();
        const getSummaries = httpsCallable(functions, 'getStripeCustomerSummaries');
        const result = await getSummaries({ customerIds });
        setCustomerSummaries(result.data?.customers || {});
      } catch (error) {
        console.error('Error loading Stripe customer summaries:', error);
        setCustomerSummaries({});
      }
    };

    loadCustomerSummaries();
  }, [organizations]);

  const loadSubscriberOrganizations = async () => {
    try {
      setLoading(true);
      const orgsRef = collection(db, 'organizations');
      
      // Start with base query - organizations with active licenses
      let q = query(
        orgsRef,
        where('license.status', 'in', ['active', 'trialing']),
        orderBy('license.createdAt', 'desc')
      );

      // Apply additional filters
      if (filter === 'early-adopter') {
        q = query(
          orgsRef,
          where('license.status', 'in', ['active', 'trialing']),
          where('license.earlyAdopter', '==', true),
          orderBy('license.createdAt', 'desc')
        );
      } else if (filter.startsWith('tier-')) {
        const planId = filter.replace('tier-', 'organization-');
        q = query(
          orgsRef,
          where('license.status', 'in', ['active', 'trialing']),
          where('license.planId', '==', planId),
          orderBy('license.createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const orgs = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.license) {
          orgs.push({
            id: doc.id,
            ...data,
          });
        }
      });

      setOrganizations(orgs);
    } catch (error) {
      console.error('Error loading subscriber organizations:', error);
      // If index error, show helpful message
      if (error.code === 'failed-precondition') {
        alert('Firestore index required. Please create the index using the link in the console error.');
      }
    } finally {
      setLoading(false);
    }
  };

  const grantEarlyAdopter = async (orgId) => {
    try {
      setProcessing(orgId);
      await updateDoc(doc(db, 'organizations', orgId), {
        'license.earlyAdopter': true,
        'license.earlyAdopterGrantedAt': serverTimestamp(),
        'license.earlyAdopterGrantedBy': currentUser.uid,
        'license.updatedAt': serverTimestamp(),
      });
      
      await loadSubscriberOrganizations();
      alert('Early adopter status granted');
    } catch (error) {
      console.error('Error granting early adopter:', error);
      alert('Error granting early adopter status');
    } finally {
      setProcessing(null);
    }
  };

  const revokeEarlyAdopter = async (orgId) => {
    try {
      setProcessing(orgId);
      await updateDoc(doc(db, 'organizations', orgId), {
        'license.earlyAdopter': false,
        'license.earlyAdopterGrantedAt': deleteField(),
        'license.earlyAdopterGrantedBy': deleteField(),
        'license.updatedAt': serverTimestamp(),
      });
      
      await loadSubscriberOrganizations();
      alert('Early adopter status revoked');
    } catch (error) {
      console.error('Error revoking early adopter:', error);
      alert('Error revoking early adopter status');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Subscriber Organizations</h1>
        <p className="text-gray-600">Manage organization subscriptions and early adopter status</p>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter Organizations
        </label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Subscribers</option>
          <option value="early-adopter">Early Adopters</option>
          <option value="tier-5">5 Licenses</option>
          <option value="tier-10">10 Licenses</option>
          <option value="tier-25">25 Licenses</option>
          <option value="tier-50">50 Licenses</option>
          <option value="tier-100">100 Licenses</option>
        </select>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-600">Total Subscribers</div>
          <div className="text-2xl font-bold">{organizations.length}</div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-600">Early Adopters</div>
          <div className="text-2xl font-bold">
            {organizations.filter((org) => org.license?.earlyAdopter).length}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-600">Total Licenses</div>
          <div className="text-2xl font-bold">
            {organizations.reduce((sum, org) => sum + (org.license?.seats || 0), 0)}
          </div>
        </div>
      </div>

      {/* Organizations List */}
      {organizations.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center">
          <p className="text-gray-500">No subscriber organizations found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {organizations.map((org) => {
            const license = org.license || {};
            const licenseCount = license.planId?.replace('organization-', '') || 'N/A';
            const availableLicenses = license.seatsAvailable || 0;
            const usedLicenses = license.seatsUsed || 0;
            const totalLicenses = license.seats || 0;
            const customer = license.stripeCustomerId
              ? customerSummaries[license.stripeCustomerId]
              : null;

            return (
              <div key={org.id} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-semibold">{org.name}</h2>
                      {license.earlyAdopter && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                          ⭐ Early Adopter
                        </span>
                      )}
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        license.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {license.status === 'active' ? 'Active' : 'Trialing'}
                      </span>
                    </div>
                    {org.tagline && (
                      <p className="text-gray-600">{org.tagline}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-600">Tier</div>
                    <div className="text-lg font-semibold">
                      {licenseCount} Licenses
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Licenses Used</div>
                    <div className="text-lg font-semibold">
                      {usedLicenses} / {totalLicenses}
                    </div>
                    <div className="text-xs text-gray-500">
                      {availableLicenses} available
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Billing Cycle</div>
                    <div className="text-lg font-semibold capitalize">
                      {license.billingCycle || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Renews</div>
                    <div className="text-lg font-semibold">
                      {formatDate(license.currentPeriodEnd)}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-md p-4 mb-4">
                  <div className="text-sm text-gray-600 mb-2">Stripe Customer</div>
                  {customer ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <div className="text-xs text-gray-500">Name</div>
                        <div className="text-sm font-medium text-gray-800">
                          {customer.name || 'Unnamed'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="text-sm font-medium text-gray-800">
                          {customer.email || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Total Spent</div>
                        <div className="text-sm font-medium text-gray-800">
                          {customer.totalPaid !== undefined
                            ? `${(customer.totalPaid / 100).toFixed(2)} ${String(customer.currency || 'usd').toUpperCase()}`
                            : 'N/A'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No Stripe customer data found.</div>
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={() => navigate(`/organization/${org.id}`)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Manage Organization
                  </button>
                  <button
                    onClick={() => navigate(`/organization/${org.id}/subscription`)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    View Subscription
                  </button>
                  {license.earlyAdopter ? (
                    <button
                      onClick={() => revokeEarlyAdopter(org.id)}
                      disabled={processing === org.id}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processing === org.id ? 'Processing...' : 'Revoke Early Adopter'}
                    </button>
                  ) : (
                    <button
                      onClick={() => grantEarlyAdopter(org.id)}
                      disabled={processing === org.id}
                      className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processing === org.id ? 'Processing...' : 'Grant Early Adopter'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubscriberOrganizations;
